// `/focus/meetings/$meetingId`: one meeting, owner only.
//
// - Under the title, "N Punkte warten auf deine Antwort" while the doubt card
//   holds items (`counts.waiting`). "Rückfragen" first (`MeetingDoubts`, the
//   doubt card): what Loom asks the owner; a held item is usually already
//   an issue ("Angelegt als #N"). An answer files or changes the issues on
//   the server, then the page reloads; one it saved but could not write to
//   the issue yet says so, with "Erneut versuchen". Then the issues it
//   filed (`MeetingReview`, mode "owner"; "Rückfrage offen" goes to the card),
//   then the meeting's own lists (`MeetingLists`: "Beschlüsse", "Zusagen",
//   "Offen geblieben"; a meeting from before the line tags, or past its 30
//   days, shows its "Beschlüsse" rows instead), then "Nicht übernommen"
//   (`NotTakenList`), then the transcript while it is kept: after the 30 days
//   the page says "Transkript nach 30 Tagen gelöscht" instead. Whether it is
//   still kept is the server's `transcriptAvailable`, and a 410 from the
//   transcript route says the same; the page compares no clocks.
// - "Projekt: ‹Name›" (the server's read) or "Kein Projekt"; "Für welches
//   Projekt?" while the meeting waits on a project; "Projekt wählen" (with
//   "Loom wartet mit dem Sortieren, bis ein Projekt gewählt ist.") while none
//   is set and "Projekt ändern" once one is, while the project may still
//   change (before triage). A launched meeting's pick also becomes its Meet
//   link's project, so the next meeting there starts with it.
// - "Verfeinern" is a COLD navigation to the issue's refine URL (a full page
//   load). It never marks a fresh refine entry (`FocusPage.tsx`
//   `markFreshRefineEntry`): this page did not just create the issue, so
//   refine must treat it like any issue the user opens by URL.
// - "Erneut versuchen" (a failed run, or a row whose filing failed) and
//   "Meeting löschen" (asks first; filed issues stay in the project).
// - "Link teilen" (`MeetingShareLink`) once the meeting filed an issue or has
//   a link: the magic link for others to review (`/m#k=…`).
// - "Neu verarbeiten" (owner decision 2026-10-18) on a `ready` or `failed`
//   meeting whose transcript is kept (the server's `reprocessable`): one
//   confirm line, then the normal phases while it runs. Afterwards the new
//   result is the page: a new row waits for "Anlegen" (or "Alle neuen
//   anlegen"), one the meeting already filed says "Schon angelegt als #N",
//   and "Frühere Auswertung" (folded) shows the earlier rows read-only.
//   A "Schon angelegt" row offers "Ticket aktualisieren" (owner decision
//   2026-10-19): a preview of the issue's body now and the whole body the
//   update writes, with a warning when the ticket was changed since; only
//   "Aktualisieren" writes it and notes the meeting on the ticket.

import { useEffect, useState } from "react";

import {
  meetingClock,
  type MeetingAnswerRequest,
  type MeetingDetailDto,
  type MeetingDoubtAnswer,
  type MeetingIssuePatchRequest,
  type MeetingIssueRowDto,
  type MeetingTranscriptDto,
  type TicketClass,
} from "@loom/shared/meetings";

import { useFormatDate } from "../../i18n/format";
import { usePlural, useT, type MessageKey } from "../../i18n/index.js";
import { HttpError } from "../../lib/http";
import { RouteNotFound } from "../../lib/RouteError";
import { fetchProjects, type Project } from "../../projects/useProjects";
import { FocusFrame, Headline, Help, Kicker, OptionButton, ThinkingDots } from "../ui";

import {
  answerMeetingDoubt,
  answerMeetingQuestion,
  deleteMeeting,
  editMeetingIssue,
  failureOf,
  fileAllMeetingTickets,
  fileMeetingTicket,
  issueAction,
  previewTicketUpdate,
  promoteTopic,
  readTranscript,
  reprocessMeeting,
  retryMeeting,
  setMeetingProject,
  updateTicket,
  type IssueAction,
} from "./meetingApi";
import {
  failureKeyOf,
  phaseKeyOf,
  PROJECT_SETTABLE,
  projectSetAwaitingRun,
  refineHrefOf,
  runLineOf,
} from "./meetingCopy";
import { MeetingDoubts } from "./MeetingDoubts";
import { MeetingLists } from "./MeetingLists";
import { openQuestionsOf } from "./MeetingQuestions";
import { MeetingReview, type RowError } from "./MeetingReview";
import { MeetingShareLink } from "./MeetingShareLink";
import { NotTakenList } from "./NotTakenList";
import { useMeeting, useMeetingsStatus } from "./useMeetings";

const DATE_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" };
const DAY_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: "medium" };

/** Statuses at which an aligned transcript existed (the server's own list). */
const TRANSCRIBED = new Set(["needs_project", "triaging", "filing", "ready"]);

/** Where "Verfeinern" lands. A full page load: nothing in memory rides along. */
export const openRefineCold = (href: string): void => {
  window.location.assign(href);
};

/**
 * "N Punkte warten auf deine Antwort": the server's count, else (a server
 * that sent none) the items of the questions it sent.
 */
export const waitingCountOf = (meeting: Pick<MeetingDetailDto, "counts" | "doubts">): number =>
  meeting.counts.waiting ?? (meeting.doubts ?? []).reduce((n, d) => n + d.items.length, 0);

/** The doubt card's question for a row's issue, else the card: "Rückfrage offen" scrolls there. */
const scrollToDoubtOf = (meeting: Pick<MeetingDetailDto, "doubts">, row: MeetingIssueRowDto): void => {
  const iid = row.issueIid ?? row.filedAs ?? null;
  const doubtId =
    (meeting.doubts ?? []).find((d) => d.items.some((i) => iid !== null && (i.filedIid ?? null) === iid))
      ?.doubtId ?? null;
  const question = [...document.querySelectorAll<HTMLElement>("[data-doubt]")].find(
    (el) => el.dataset.doubt === doubtId,
  );
  const target = question ?? document.querySelector<HTMLElement>(".meet-doubts");
  target?.scrollIntoView?.({ behavior: "smooth", block: "start" });
};

/** The owner's project pick: the recent list, or a search. */
function ProjectPick({
  busy,
  onPick,
  onCancel,
}: {
  readonly busy: boolean;
  readonly onPick: (project: Project) => void;
  readonly onCancel?: () => void;
}) {
  const t = useT();
  const [query, setQuery] = useState("");
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    const q = query.trim();
    const run = (): void => {
      fetchProjects(q === "" ? { recent: true, includeGithub: true } : { search: q, includeGithub: true })
        .then((next) => {
          if (!alive) return;
          setProjects(next);
          setFailed(false);
        })
        .catch(() => {
          if (alive) setFailed(true);
        });
    };
    if (q === "") {
      run();
      return () => {
        alive = false;
      };
    }
    const handle = setTimeout(run, 250);
    return () => {
      alive = false;
      clearTimeout(handle);
    };
  }, [query]);
  return (
    <div className="meet-pick">
      <input
        className="meet-edit-input"
        aria-label={t("focus.project.searchAria")}
        placeholder={t("focus.project.searchPlaceholder")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {projects === null && !failed && <ThinkingDots label={t("common.loading")} />}
      {failed && <div className="focus-error">{t("meetings.error.generic")}</div>}
      {projects !== null && (
        <div className="focus-options">
          {projects.slice(0, 8).map((p) => (
            <OptionButton
              key={p.id}
              label={p.name_with_namespace}
              onClick={() => {
                if (!busy) onPick(p);
              }}
            />
          ))}
        </div>
      )}
      {onCancel !== undefined && (
        <button type="button" className="focus-deeplink" onClick={onCancel}>
          {t("common.cancel")}
        </button>
      )}
    </div>
  );
}

function Transcript({ meeting }: { readonly meeting: MeetingDetailDto }) {
  const t = useT();
  const day = useFormatDate(DAY_FORMAT);
  const [shown, setShown] = useState(false);
  const [data, setData] = useState<MeetingTranscriptDto | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "expired" | "failed">("idle");

  const expired =
    state === "expired" || (!meeting.transcriptAvailable && TRANSCRIBED.has(meeting.status));
  if (expired) return <p className="meet-meta">{t("meetings.transcript.expired")}</p>;
  if (!meeting.transcriptAvailable) return null;

  const toggle = (): void => {
    if (shown) {
      setShown(false);
      return;
    }
    setShown(true);
    if (data !== null) return;
    setState("loading");
    readTranscript(meeting.meetingId)
      .then((res) => {
        setData(res);
        setState("idle");
      })
      .catch((err: unknown) => {
        setState(err instanceof HttpError && err.status === 410 ? "expired" : "failed");
      });
  };

  return (
    <section className="meet-section">
      <div className="meet-actions">
        <button type="button" className="focus-deeplink" onClick={toggle}>
          {t(shown ? "meetings.transcript.hide" : "meetings.transcript.show")}
        </button>
        {meeting.contentExpiresAt !== null && (
          <span className="meet-meta">
            {t("meetings.transcript.until", { date: day(meeting.contentExpiresAt) })}
          </span>
        )}
      </div>
      {shown && state === "loading" && <ThinkingDots label={t("common.loading")} />}
      {shown && state === "failed" && (
        <div className="focus-error">{t("meetings.transcript.loadFailed")}</div>
      )}
      {shown && data !== null && (
        <ol className="meet-transcript">
          {data.lines.map((line, i) => (
            <li key={i}>
              <span className="meet-meta">
                {/* A line no speaker was matched to has only its chunk's start: no time. */}
                {line.speakerLabel === null
                  ? t("meetings.quote.unknownSpeaker")
                  : `${meetingClock(line.approxMs)} ${line.speakerLabel}`}
              </span>{" "}
              {line.text}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export function MeetingPage({
  meetingId,
  onBack,
  onDeleted,
  openCold = openRefineCold,
}: {
  readonly meetingId: string;
  /** Back to its project's meetings (`projectId`), or the projects while unknown. */
  readonly onBack: (projectId: number | null) => void;
  /** After "Meeting löschen": back to its project's list. */
  readonly onDeleted: (projectId: number | null) => void;
  /** "Verfeinern": a full page load of the refine URL. A test seam only. */
  readonly openCold?: (href: string) => void;
}) {
  const t = useT();
  const tn = usePlural();
  const when = useFormatDate(DATE_FORMAT);
  const { status } = useMeetingsStatus();
  const enabled = status?.enabled === true;
  const { load, reload, replaceIssue } = useMeeting(meetingId, enabled);
  const [busyTicketId, setBusyTicketId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<RowError | null>(null);
  const [busyTopicId, setBusyTopicId] = useState<string | null>(null);
  const [busyDoubtId, setBusyDoubtId] = useState<string | null>(null);
  // `retry`: the note offers "Erneut versuchen" (an answer not in its issue yet).
  const [pageNote, setPageNote] = useState<{
    readonly key: MessageKey;
    readonly bad: boolean;
    readonly retry?: boolean;
  } | null>(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReprocess, setConfirmReprocess] = useState(false);

  if (status === null || (enabled && load.kind === "loading")) {
    return (
      <FocusFrame stepKey="meeting-loading" onBack={() => onBack(null)}>
        <ThinkingDots label={t("common.loading")} />
      </FocusFrame>
    );
  }
  if (!enabled) return <RouteNotFound />;
  if (load.kind !== "ready") {
    return (
      <FocusFrame stepKey="meeting-missing" onBack={() => onBack(null)}>
        <div className="focus-error" role="alert">
          {t(load.kind === "not_found" ? "meetings.detail.notFound" : "meetings.detail.loadFailed")}
        </div>
      </FocusFrame>
    );
  }
  const meeting = load.meeting;

  const runAction = (row: MeetingIssueRowDto, action: IssueAction): void => {
    if (busyTicketId !== null || row.issueIid === null) return;
    setBusyTicketId(row.ticketId);
    setRowError(null);
    issueAction(meeting.meetingId, row.issueIid, action)
      .then(replaceIssue)
      .catch((err: unknown) => {
        const f = failureOf(err);
        if (f.issue !== null) replaceIssue(f.issue);
        setRowError({ ticketId: row.ticketId, key: failureKeyOf(f) });
      })
      .finally(() => setBusyTicketId(null));
  };

  const saveEdit = async (row: MeetingIssueRowDto, patch: MeetingIssuePatchRequest): Promise<boolean> => {
    if (busyTicketId !== null || row.issueIid === null) return false;
    setBusyTicketId(row.ticketId);
    setRowError(null);
    try {
      replaceIssue(await editMeetingIssue(meeting.meetingId, row.issueIid, patch));
      return true;
    } catch (err) {
      const f = failureOf(err);
      if (f.issue !== null) replaceIssue(f.issue);
      setRowError({ ticketId: row.ticketId, key: failureKeyOf(f) });
      return false;
    } finally {
      setBusyTicketId(null);
    }
  };

  /** An open question answered on the owner's page; true when saved. */
  const answer = async (
    row: MeetingIssueRowDto,
    field: string,
    body: MeetingAnswerRequest,
  ): Promise<boolean> => {
    if (row.issueIid === null) return false;
    setRowError(null);
    try {
      replaceIssue(await answerMeetingQuestion(meeting.meetingId, row.issueIid, field, body));
      return true;
    } catch (err) {
      const f = failureOf(err);
      if (f.issue !== null) {
        replaceIssue(f.issue);
        setRowError({ ticketId: row.ticketId, key: failureKeyOf(f) });
      }
      return false;
    }
  };

  const promote = (topicId: string, as: TicketClass): void => {
    if (busyTopicId !== null) return;
    setBusyTopicId(topicId);
    setPageNote(null);
    promoteTopic(meeting.meetingId, topicId, as)
      .then((row) => {
        replaceIssue(row);
        reload();
      })
      .catch((err: unknown) => setPageNote({ key: failureKeyOf(failureOf(err)), bad: true }))
      .finally(() => setBusyTopicId(null));
  };

  /** The doubt card: an answer files, changes or leaves out the items it held. */
  const answerDoubt = (doubtId: string, answer: MeetingDoubtAnswer, leaveOut: readonly string[]): void => {
    if (busyDoubtId !== null) return;
    setBusyDoubtId(doubtId);
    setPageNote(null);
    answerMeetingDoubt(meeting.meetingId, doubtId, answer, leaveOut)
      .then((res) => {
        // Saved, but an issue it changes did not take it yet: "Erneut versuchen" writes it.
        if ((res.effectFailed ?? 0) > 0) {
          setPageNote({ key: "meetings.doubts.result.effectFailed", bad: true, retry: true });
        }
        reload();
      })
      .catch((err: unknown) => {
        const f = failureOf(err);
        // The question no longer waits (answered meanwhile): say so, show the page as it is.
        setPageNote({ key: f.status === 404 ? "meetings.error.changed" : failureKeyOf(f), bad: true });
        reload();
      })
      .finally(() => setBusyDoubtId(null));
  };

  const retry = (): void => {
    if (busy) return;
    setBusy(true);
    setPageNote(null);
    retryMeeting(meeting.meetingId)
      .then((res) => {
        setPageNote({
          key: res.outcome === "restarted" ? "meetings.retry.restarted" : "meetings.retry.filed",
          bad: false,
        });
        reload();
      })
      .catch((err: unknown) => setPageNote({ key: failureKeyOf(failureOf(err)), bad: true }))
      .finally(() => setBusy(false));
  };

  const pickProject = (project: Project): void => {
    if (busy) return;
    setBusy(true);
    setPageNote(null);
    // A meeting parked for its project goes on at the next processing tick
    // (the server wakes the run); say so at once, or the pick looks like a
    // click that did nothing until the status moves.
    const wasWaiting = meeting.status === "needs_project";
    setMeetingProject(meeting.meetingId, project.id)
      .then(() => {
        setPicking(false);
        if (wasWaiting) setPageNote({ key: "meetings.project.setPending", bad: false });
        reload();
      })
      .catch((err: unknown) => setPageNote({ key: failureKeyOf(failureOf(err)), bad: true }))
      .finally(() => setBusy(false));
  };

  /** "Anlegen": file one new row of a reprocess run. */
  const fileOne = (row: MeetingIssueRowDto): void => {
    if (busyTicketId !== null) return;
    setBusyTicketId(row.ticketId);
    setRowError(null);
    fileMeetingTicket(meeting.meetingId, row.ticketId)
      .then(replaceIssue)
      .catch((err: unknown) => {
        setRowError({ ticketId: row.ticketId, key: failureKeyOf(failureOf(err)) });
        reload();
      })
      .finally(() => setBusyTicketId(null));
  };

  /** "Ticket aktualisieren", confirmed: null when it went through, else the line to show. */
  const confirmUpdate = async (row: MeetingIssueRowDto, currentBodySha256: string): Promise<MessageKey | null> => {
    if (busyTicketId !== null) return "meetings.error.updateRunning";
    setBusyTicketId(row.ticketId);
    setRowError(null);
    try {
      replaceIssue(await updateTicket(meeting.meetingId, row.ticketId, currentBodySha256));
      return null;
    } catch (err) {
      const f = failureOf(err);
      // The ticket changed since the preview, or the row no longer updates: show the page as it is.
      if (f.status === 409 || f.status === 404) reload();
      return failureKeyOf(f);
    } finally {
      setBusyTicketId(null);
    }
  };

  /** "Alle neuen anlegen". */
  const fileAll = (): void => {
    if (busy) return;
    setBusy(true);
    setPageNote(null);
    fileAllMeetingTickets(meeting.meetingId)
      .then((res) => {
        if (res.failed > 0) setPageNote({ key: "meetings.fileAll.partial", bad: true });
        reload();
      })
      .catch((err: unknown) => setPageNote({ key: failureKeyOf(failureOf(err)), bad: true }))
      .finally(() => setBusy(false));
  };

  /** "Neu verarbeiten": the run starts again with the current triage; nothing is filed. */
  const reprocess = (): void => {
    if (busy) return;
    setBusy(true);
    setPageNote(null);
    reprocessMeeting(meeting.meetingId)
      .then(() => {
        setConfirmReprocess(false);
        reload();
      })
      .catch((err: unknown) => setPageNote({ key: failureKeyOf(failureOf(err)), bad: true }))
      .finally(() => setBusy(false));
  };

  const remove = (): void => {
    if (busy) return;
    setBusy(true);
    setPageNote(null);
    deleteMeeting(meeting.meetingId)
      .then(() => onDeleted(meeting.projectId))
      .catch((err: unknown) => {
        setPageNote({ key: failureKeyOf(failureOf(err)), bad: true });
        setBusy(false);
      });
  };

  const phase = phaseKeyOf(meeting);
  const projectId = meeting.projectId;
  // The picker is the question only while no project is set. A pick the run
  // has not picked up yet (still `needs_project`, project set) is a status
  // line, not the picker again.
  const needsProject = meeting.status === "needs_project" && projectId === null;
  const projectSetPending = projectSetAwaitingRun(meeting);
  // A meeting that can never be processed: no recording once it closed (the
  // reconcile never looks again), or Google's record of it gone. A project
  // would change nothing there.
  const terminal =
    meeting.status === "no_recording" ||
    (meeting.status === "failed" && meeting.failureCode === "content_expired");
  const canChangeProject = PROJECT_SETTABLE.has(meeting.status) && !terminal;
  // "Projekt gesetzt …" holds only while the run still waits for its tick;
  // once polling shows the meeting moved on, the note would be stale.
  const shownNote =
    pageNote !== null && (pageNote.key !== "meetings.project.setPending" || meeting.status === "needs_project")
      ? pageNote
      : null;
  // "Projekt: ‹Name›" from the server's read (owner's token), "Kein
  // Projekt" while none is set; a project whose name the server could not
  // read gets no line, only the button.
  const projectName = meeting.projectName ?? null;
  const projectLine =
    projectId === null
      ? t("meetings.project.none")
      : projectName !== null
        ? t("meetings.project.current", { project: projectName })
        : null;
  const failureLine =
    meeting.failureCode !== null ? (`error.${meeting.failureCode}` as MessageKey) : null;
  // The run's step, its attempt and why the last one failed (server facts).
  const runLine = runLineOf(meeting.run);
  const decisions = meeting.decisions;
  const lists = meeting.lists ?? null;
  const superseded = meeting.superseded ?? null;
  const fileableCount = meeting.issues.filter((i) => i.fileable === true).length;
  const waiting = waitingCountOf(meeting);
  // Issues in the project: filed now, filed by an earlier result, or already filed as one.
  const anyFiled =
    meeting.issues.some((i) => i.state === "filed" || (i.filedAs ?? null) !== null) ||
    (superseded?.issues ?? []).some((i) => i.state === "filed");

  return (
    <FocusFrame stepKey={`meeting-${meeting.meetingId}`} onBack={() => onBack(projectId)} wide>
      <Kicker>{when(meeting.startedAt ?? meeting.createdAt)}</Kicker>
      <Headline>{meeting.title ?? t("meetings.row.untitled")}</Headline>
      <div className="meet-row-head">
        {phase !== null && <span className={`focus-chip meet-phase is-${meeting.status}`}>{t(phase)}</span>}
        {meeting.recordingFlag === "incomplete" && (
          <span className="focus-chip">{t("meetings.phase.incomplete")}</span>
        )}
        {meeting.spontaneous && <span className="focus-chip">{t("meetings.row.spontaneous")}</span>}
      </div>
      {waiting > 0 && <p className="meet-meta">{tn("plural.meetingWaiting", waiting)}</p>}
      {failureLine !== null && <Help>{t(failureLine)}</Help>}
      {runLine !== null && (
        <div className="focus-help" role="status">
          {t(runLine.key, {
            step: runLine.stepKey === null ? "" : t(runLine.stepKey),
            reason: runLine.reasonKey === null ? "" : t(runLine.reasonKey),
            n: runLine.n,
            max: runLine.max,
          })}
        </div>
      )}

      {shownNote !== null && (
        <div className={shownNote.bad ? "focus-error" : "focus-help"} role={shownNote.bad ? "alert" : "status"}>
          {t(shownNote.key)}
          {shownNote.retry === true && (
            <>
              {" "}
              <button type="button" className="focus-deeplink" disabled={busy} onClick={retry}>
                {t("meetings.action.retry")}
              </button>
            </>
          )}
        </div>
      )}

      {canChangeProject && (needsProject || picking) ? (
        <section className="meet-section">
          <h2 className="meet-section-title">{t("meetings.phase.needsProject")}</h2>
          <ProjectPick
            busy={busy}
            onPick={pickProject}
            {...(needsProject ? {} : { onCancel: () => setPicking(false) })}
          />
        </section>
      ) : (
        <div className="meet-project">
          {projectLine !== null && <span className="meet-meta">{projectLine}</span>}
          {canChangeProject && (
            <button type="button" className="focus-deeplink" onClick={() => setPicking(true)}>
              {t(projectId === null ? "meetings.project.pick" : "meetings.project.change")}
            </button>
          )}
        </div>
      )}
      {canChangeProject && projectId === null && !needsProject && !picking && (
        <Help>{t("meetings.project.waitHint")}</Help>
      )}
      {projectSetPending && !picking && shownNote?.key !== "meetings.project.setPending" && (
        <div className="focus-help" role="status">
          {t("meetings.project.setPending")}
        </div>
      )}

      <MeetingDoubts
        doubts={meeting.doubts ?? []}
        pastCap={meeting.doubtsPastCap ?? 0}
        settled={meeting.doubtsSettled ?? []}
        busyDoubtId={busyDoubtId}
        {...(projectId === null ? {} : { onAnswer: answerDoubt })}
      />

      {(meeting.issues.length > 0 || meeting.status === "ready") && (
        <section className="meet-section">
          <h2 className="meet-section-title">{t("meetings.detail.issues")}</h2>
          {fileableCount > 0 && (
            <div className="meet-actions">
              <button type="button" className="focus-btn" disabled={busy || busyTicketId !== null} onClick={fileAll}>
                {t("meetings.action.fileAll")}
              </button>
            </div>
          )}
          {meeting.issues.length === 0 ? (
            meeting.status === "ready" && <Help>{t("meetings.detail.noIssues")}</Help>
          ) : (
            <MeetingReview
              mode="owner"
              issues={meeting.issues}
              busyTicketId={busyTicketId}
              rowError={rowError}
              onAction={runAction}
              onSaveEdit={saveEdit}
              onRetry={retry}
              onAnswer={answer}
              onFile={fileOne}
              onPreviewUpdate={(row) => previewTicketUpdate(meeting.meetingId, row.ticketId)}
              onConfirmUpdate={confirmUpdate}
              onOpenDoubt={(row) => scrollToDoubtOf(meeting, row)}
              {...(projectId === null
                ? {}
                : {
                    onRefine: (row: MeetingIssueRowDto) => {
                      if (row.issueIid !== null) openCold(refineHrefOf(projectId, row.issueIid));
                    },
                  })}
            />
          )}
        </section>
      )}

      {(anyFiled || (meeting.link ?? null) !== null) && (
        <MeetingShareLink
          meetingId={meeting.meetingId}
          link={meeting.link ?? null}
          onChanged={reload}
          questionsOpen={meeting.issues.some((i) => openQuestionsOf(i).length > 0)}
        />
      )}

      {lists !== null && <MeetingLists lists={lists} />}

      {lists === null && decisions.length > 0 && (
        <section className="meet-section">
          <h2 className="meet-section-title">{t("meetings.decisions.title")}</h2>
          <ul className="meet-list">
            {decisions.map((d) => (
              <li key={d.topicId} className="meet-row">
                {d.summary !== null && <span>{d.summary}</span>}{" "}
                <span className="meet-meta">{t("meetings.quote.at", { clock: meetingClock(d.startMs) })}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <NotTakenList
        topics={meeting.notTaken}
        busyTopicId={busyTopicId}
        {...(projectId === null ? {} : { onPromote: promote })}
      />

      {superseded !== null && (
        <details className="meet-fold meet-section meet-superseded">
          <summary>{t("meetings.superseded.title")}</summary>
          {superseded.issues.length > 0 && (
            <MeetingReview
              mode="owner"
              readOnly
              issues={superseded.issues}
              busyTicketId={null}
              rowError={null}
              onAction={() => {}}
              onSaveEdit={() => Promise.resolve(false)}
              {...(projectId === null
                ? {}
                : {
                    onRefine: (row: MeetingIssueRowDto) => {
                      if (row.issueIid !== null) openCold(refineHrefOf(projectId, row.issueIid));
                    },
                  })}
            />
          )}
          <NotTakenList topics={superseded.notTaken} busyTopicId={null} />
        </details>
      )}

      <Transcript meeting={meeting} />

      <div className="meet-actions meet-owner">
        {meeting.status === "failed" && (
          <button type="button" className="focus-btn is-ghost" disabled={busy} onClick={retry}>
            {t("meetings.action.retry")}
          </button>
        )}
        {meeting.reprocessable === true &&
          (confirmReprocess ? (
            <div className="meet-confirm" role="group" aria-label={t("meetings.reprocess")}>
              <p className="focus-help">{t("meetings.reprocess.confirm")}</p>
              <div className="meet-actions">
                <button type="button" className="focus-btn" disabled={busy} onClick={reprocess}>
                  {t("meetings.reprocess")}
                </button>
                <button
                  type="button"
                  className="focus-btn is-ghost"
                  disabled={busy}
                  onClick={() => setConfirmReprocess(false)}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="focus-btn is-ghost" disabled={busy} onClick={() => setConfirmReprocess(true)}>
              {t("meetings.reprocess")}
            </button>
          ))}
        {confirmDelete ? (
          <div className="meet-confirm" role="group" aria-label={t("meetings.delete")}>
            <p className="focus-help">{t("meetings.delete.confirm")}</p>
            <div className="meet-actions">
              <button type="button" className="focus-btn" disabled={busy} onClick={remove}>
                {t("meetings.delete.yes")}
              </button>
              <button
                type="button"
                className="focus-btn is-ghost"
                disabled={busy}
                onClick={() => setConfirmDelete(false)}
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="focus-deeplink" onClick={() => setConfirmDelete(true)}>
            {t("meetings.delete")}
          </button>
        )}
      </div>
    </FocusFrame>
  );
}
